import * as Accountant from "app/calcs/Accountant"
import { Local697PensionCalculations } from "app/calcs/customers/local697"
import {
    ILocal697PensionInputs,
    Local697FormOfPayment,
} from "app/calcs/customers/local697/interfaces"
import { FormOfPaymentOptions } from "app/calcs/pensions"
import {
    AllFormsOfPayment,
    IMonthlyPensionPayments,
    SimplePensionFormOfPayment,
} from "app/calcs/pensions/interfaces"
import { MaterialInput, RadioGroup } from "app/components"
import { NumberSelector } from "app/components/form/NumberSelector"
import { RetirementAgeSelector } from "app/components/form/RetirementAgeSelector"
import { Infobox } from "app/components/Infobox"
import MaterialNumber from "app/components/material/MaterialNumber"
import { FormOfPayment } from "app/components/questions/FormOfPayment"
import {
    IProfileContainerState,
    PensionQuestionsGroupContainer,
} from "app/containers/PipelineSection/Pension/Container"
import {
    IQuestionsGroupContainerProps,
    QuestionsGroupContainer,
} from "app/containers/PipelineSection/QuestionGroupContainer"
import { cachedLocal697ExportExhibitTab } from "app/redux/Customers/local697/actions"
import {
    dateAtAge,
    IRetirementPerson,
    isSamePersonForSSA,
    Profile,
} from "app/redux/Profile/model"
import { theCensusInfo } from "app/services/models/PublicCensusInfo"
import { WorkbookServiceResponse } from "app/services/models/Workbook"
import * as utils from "app/utils"
import { RequestCache } from "app/utils/RequestCache"
import * as Config from "config"
import * as _ from "lodash"
import * as moment from "moment"
import * as React from "react"
import {
    Button,
    ButtonGroup,
    Col,
    FormGroup,
    ListGroup,
    ListGroupItem,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap"

enum ExhibitGenerationState {
    idle,
    generating,
    ready,
}

interface ILocal697PensionContainerState extends IProfileContainerState {
    headOfHousehold: IRetirementPerson
    monthlyPayout: number
    totalLumpSum: number
    totalVestingService: number
    totalPensionCredits: number
    exhibitGeneration: ExhibitGenerationState
    downloadFilename: string

    // these are synced with profile.pension.local697
    beginPaymentsAge: moment.Duration
    formOfPayment: Local697FormOfPayment
    hoursWorked: number
    payRate: number
    percentLumpSumElected: number
}

class Local697PensionContainer extends PensionQuestionsGroupContainer<
    IQuestionsGroupContainerProps,
    ILocal697PensionContainerState
> {
    beginPaymentsAgeNS = "pension.local697.beginPaymentsAge"
    hoursWorkedNS = "pension.shared.hoursWorked"
    payRateNS = "pension.local697.payRate"
    formOfPaymentNS = "pension.local697.formOfPayment"
    percentLumpSumElectedNS = "pension.local697.percentLumpSumElected"

    state: ILocal697PensionContainerState = {
        profile: null,
        headOfHousehold: null,
        formOfPayment:
            this.getValueForQuestion(this.formOfPaymentNS) ||
            SimplePensionFormOfPayment.singleLifeOnly,
        payRate: this.getValueForQuestion(this.payRateNS) || 0,
        hoursWorked: this.getValueForQuestion(this.hoursWorkedNS) || 0,
        monthlyPayout: 0,
        beginPaymentsAge:
            this.getValueForQuestion(this.beginPaymentsAgeNS) || null,
        percentLumpSumElected:
            this.getValueForQuestion(this.percentLumpSumElectedNS) || 0,
        totalLumpSum: 0,
        totalVestingService: 0,
        totalPensionCredits: 0,
        exhibitGeneration: ExhibitGenerationState.idle,
        downloadFilename: null,
    }

    initCalc = false

    debounceCalculations = _.debounce(() => {
        // tslint:disable-next-line:no-console
        //console.log('local697.debounceCalculations entered')
        const person = this.state.headOfHousehold
        const profile = this.state.profile
        const inputs = this.state.profile.pension.local697
        const { payRate, beginPaymentsAge, formOfPayment } = this.state
        const when = dateAtAge(person, beginPaymentsAge)

        const computer = new Local697PensionCalculations(this.state.profile)
        computer.setSpouse(profile.getSpouse())
        computer
            .getMonthlyPensionAmount(person, inputs, when)
            .then((payout) => {
                const {
                    amount,
                    totalLumpSum,
                    totalPensionCredits,
                    totalVestingService,
                } = payout
                this.setState({
                    monthlyPayout: amount,
                    totalLumpSum,
                    totalVestingService,
                    totalPensionCredits,
                })
            })
    }, 300)

    componentWillMount() {
        this.onAppStateUpdate((appState) => {
            const profile = appState.Profile.getNative("info")
            const headOfHousehold = profile.getHeadOfHousehold()

            if (profile.pension.local697) {
                this.debounceCalculations()
            }

            this.setState({ headOfHousehold, profile })
        })
    }

    componentWillUpdate(
        nextProps: IQuestionsGroupContainerProps,
        nextState: ILocal697PensionContainerState
    ) {
        this.updateCalculations(nextState)
    }

    onExportExhibits() {
        this.setState({ exhibitGeneration: ExhibitGenerationState.generating })
        const profile = this.state.profile
        const member = profile.getHeadOfHousehold()
        const spouse = profile.getSpouse()
        const shared = profile.pension.shared
        const vals = profile.pension.local697
        const ioInputs = profile.pension.IO
        const nebfInputs = profile.pension.NEBF
        cachedLocal697ExportExhibitTab({
            member,
            spouse,
            vals,
            ioInputs,
            nebfInputs,
            shared,
            when: null,
        })
            // NOTE: I don't know why this comes back as a workbook response
            // that has 'message' at the wrong level....
            .then((xx: any) => {
                this.setState({
                    exhibitGeneration: ExhibitGenerationState.ready,
                })
                const endpoint = Config.getWebServicesConfig().miscEndpoint
                const downloadFilename =
                    endpoint +
                    "api/misc/downloadTransactionFile?path=" +
                    xx.Message
                this.setState({ downloadFilename })
            })
    }

    updateCalculations(nextState: ILocal697PensionContainerState) {
        const {
            profile,
            formOfPayment,
            headOfHousehold,
            hoursWorked,
            payRate,
            beginPaymentsAge,
        } = nextState

        if (!profile) {
            return
        }

        profile.pension = profile.pension || {}
        nextState.beginPaymentsAge =
            nextState.beginPaymentsAge ||
            moment.duration(headOfHousehold.selectedRetirementAge, "years")

        const updates = {
            beginPaymentsAge,
            formOfPayment,
            hoursWorked,
            payRate,
        }
        const newLocal697 = _.defaults({}, updates, profile.pension.local697)

        if (
            !this.initCalc ||
            !_.isEqual(profile.pension.local697, newLocal697)
        ) {
            this.initCalc = true
            profile.pension.local697 = newLocal697
            this.debounceCalculations()
        }
    }

    updateHoursWorked(hoursWorked: number) {
        this.state.profile.pension.shared.hoursWorked = hoursWorked
        const exhibitGeneration = ExhibitGenerationState.idle
        this.setStateAndSave({ hoursWorked, exhibitGeneration })
    }

    updateLumpSum(percentLumpSumElected: number) {
        this.state.profile.pension.local697.percentLumpSumElected =
            percentLumpSumElected
        const exhibitGeneration = ExhibitGenerationState.idle
        this.setStateAndSave({ percentLumpSumElected, exhibitGeneration })
    }

    updatePaymentsAge(beginPaymentsAge: moment.Duration) {
        this.state.profile.pension.local697.beginPaymentsAge = beginPaymentsAge
        const exhibitGeneration = ExhibitGenerationState.idle
        this.setState({ beginPaymentsAge, exhibitGeneration })

        this.markSignificantProfileUpdate(this.state.profile)
    }

    renderExhibitGeneration() {
        switch (this.state.exhibitGeneration) {
            case ExhibitGenerationState.idle: {
                return (
                    <div>
                        For information on benefits at other projected ages,
                        click&nbsp;
                        <a
                            onClick={() => {
                                this.onExportExhibits()
                            }}
                        >
                            here
                        </a>{" "}
                        to prepare the analysis.
                    </div>
                )
            }
            case ExhibitGenerationState.generating: {
                return (
                    <div>Please wait while your report is being generated.</div>
                )
            }
            case ExhibitGenerationState.ready:
            default: {
                return (
                    <div>
                        Your report is ready for download. Click to{" "}
                        <a href={this.state.downloadFilename}>here</a> to
                        download it.
                    </div>
                )
            }
        }
    }

    render() {
        if (!this.state.headOfHousehold) {
            return null
        }
        if (
            !this.state.profile.pension ||
            !this.state.profile.pension.local697 ||
            !this.state.beginPaymentsAge
        ) {
            return null
        }

        const censusDateText = theCensusInfo.censusDate.format("LL")

        const profile = this.state.profile
        const spouseIsValid = profile.spouseIsValid()
        const spouse = profile.getSpouse()
        const local697 = profile.pension.local697

        const beginPaymentsAgeText = utils.toNiceAge(
            this.state.beginPaymentsAge
        )
        const showSummary = this.state.monthlyPayout !== 0
        const monthlyPayoutText = utils.toCurrency(this.state.monthlyPayout)

        const totalVestingServiceText =
            this.state.totalVestingService.toFixed(0)
        const totalPensionCreditsText =
            this.state.totalPensionCredits.toFixed(1)
        const showLumpSum = 0 !== local697.percentLumpSumElected
        const aStyle = {
            marginBottom: "10px",
        }

        return (
            <section className="animated fadeIn">
                <h1>Local 697 Pension</h1>
                <h5 className="lead" style={aStyle}>
                    We have the following information from your records. This
                    information is current as of {censusDateText}.{" "}
                    {theCensusInfo.publicNotes}
                </h5>
                <ul>
                    <li>
                        Local 697 Pension Credits:{" "}
                        <a>{local697.futureServiceCredits} years</a>
                    </li>
                    <li>
                        Local 697 Accrued Benefit:{" "}
                        <a>{utils.toCurrency(local697.monthlyBen)}</a>
                    </li>
                </ul>

                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <RetirementAgeSelector
                                min={55}
                                max={70}
                                placeholder="What age will you begin payments?"
                                selectedValue={this.state.beginPaymentsAge}
                                onChange={(a) => this.updatePaymentsAge(a)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <NumberSelector
                                min={0}
                                max={10}
                                width="100%"
                                anyPlaceholder={
                                    <span>
                                        What percentage of your benefit will you
                                        take as a lump sum?
                                        <Infobox>
                                            Option to choose any percentage
                                            between 0% and 10%. 0% means no lump
                                            sum is paid and your full benefit is
                                            paid as an annuity. Note that lump
                                            sums are determined based on current
                                            interest rates, which may not be the
                                            rate in effect when the actual lump
                                            sum is paid.
                                        </Infobox>
                                    </span>
                                }
                                selectedValue={this.state.percentLumpSumElected}
                                onChange={(a) => this.updateLumpSum(a)}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        {showSummary && (
                            <h2 className="animated fadeIn">
                                If {this.state.headOfHousehold.firstName} begins
                                to take payments at age&nbsp;
                                <a>{beginPaymentsAgeText}</a>&nbsp; after{" "}
                                <a>{totalPensionCreditsText}</a> years of
                                service,
                                {showLumpSum && (
                                    <span>
                                        {" "}
                                        the lump sum will be{" "}
                                        <a>
                                            {utils.toCurrency(
                                                this.state.totalLumpSum
                                            )}
                                        </a>{" "}
                                        and
                                    </span>
                                )}
                                &nbsp;the monthly amount will be{" "}
                                <a>{monthlyPayoutText}</a>.
                                {spouseIsValid && (
                                    <span>
                                        &nbsp;Upon death, {spouse.firstName}{" "}
                                        will continue receiving this monthly
                                        amount for life.
                                    </span>
                                )}
                            </h2>
                        )}
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>{this.renderExhibitGeneration()}</Col>
                </Row>
            </section>
        )
    }
}

const Local697PensionQuestionsGroup = () => (
    <Local697PensionContainer header="Local 697 Pension" icon="" />
)

export { Local697PensionQuestionsGroup }



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/pension/Local697/index.tsx